import type { Route } from '../router.ts';
import { contrastOfDay, siddisOfDay, tracks } from '../data/sample-today.ts';
import { icons } from '../icons.ts';
import { speak } from '../data/speech.ts';

export function renderAside(_route: Route): HTMLElement {
  const aside = document.createElement('aside');
  aside.className = 'dashboard-aside flex flex-col gap-4 p-6 border-l border-[color:var(--color-border)] overflow-y-auto';
  aside.style.gridArea = 'aside';

  aside.append(renderContrastCard());
  aside.append(renderStavangerskCard());
  aside.append(renderRadar());

  return aside;
}

function renderContrastCard(): HTMLElement {
  const c = document.createElement('div');
  c.className = 'rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-elevated)] p-4';
  const contrast = contrastOfDay();
  const examplesHtml = contrast.examples
    .map((e) => `<li><span class="font-medium text-[color:var(--color-fjord-700)]">${e.norwegian}</span> = ${e.french}</li>`)
    .join('');
  c.innerHTML = `
    <div class="flex items-center gap-2 mb-2">
      <span class="w-7 h-7 rounded-full bg-[color:var(--tint-butter-bg)] text-[color:var(--tint-butter-ink)] flex items-center justify-center"><span class="w-4 h-4 inline-block">${icons.bulb}</span></span>
      <h3 class="text-sm font-medium">Saviez-vous&nbsp;?</h3>
    </div>
    <p class="text-sm mb-3">${contrast.trap}</p>
    <p class="text-xs text-[color:var(--color-ink-muted)] mb-1">Exemples&nbsp;:</p>
    <ul class="text-xs space-y-1 list-none">${examplesHtml}</ul>
  `;
  return c;
}

function renderStavangerskCard(): HTMLElement {
  const c = document.createElement('div');
  c.className = 'rounded-xl border border-[color:var(--color-stavanger)] bg-[color:var(--color-stavanger)]/30 p-4';
  const siddis = siddisOfDay();
  c.innerHTML = `
    <div class="flex items-center gap-2 mb-2">
      <span class="w-7 h-7 rounded-full bg-[color:var(--tint-rose-bg)] text-[color:var(--tint-rose-ink)] flex items-center justify-center"><span class="w-4 h-4 inline-block">${icons.anchor}</span></span>
      <h3 class="text-sm font-medium">Siddis du jour <span class="text-[10px] font-normal opacity-70">(dialecte de Stavanger)</span></h3>
    </div>
    <p class="text-sm">${siddis.bokmal}</p>
    <p class="text-xs text-[color:var(--color-ink-muted)] mb-2">→ En siddis&nbsp;:</p>
    <p class="text-base font-medium text-[color:var(--color-rorbu)] mb-3">${siddis.stavangersk}</p>
    <button data-play-bokmal class="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md bg-[color:var(--color-surface-elevated)] hover:bg-white/80 text-xs mb-2 transition-colors">
      <span class="flex items-center gap-2"><span class="w-3.5 h-3.5">${icons.volume}</span>Bokmål standard</span>
      <span class="w-3 h-3">${icons.play}</span>
    </button>
    <button data-play-siddis class="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md bg-[color:var(--color-surface-elevated)] hover:bg-white/80 text-xs transition-colors">
      <span class="flex flex-col items-start gap-0.5">
        <span class="flex items-center gap-2"><span class="w-3.5 h-3.5">${icons.volume}</span>Voir le siddis</span>
        <span class="text-[9px] opacity-70 font-normal">lecture en bokmål — pas d'audio siddis dans les corpus libres</span>
      </span>
      <span class="w-3 h-3">${icons.play}</span>
    </button>
  `;
  c.querySelector('[data-play-bokmal]')?.addEventListener('click', () => { void speak(siddis.bokmal, { voiceIndex: 0 }); });
  c.querySelector('[data-play-siddis]')?.addEventListener('click', () => { void speak(siddis.bokmal, { voiceIndex: 0 }); });
  return c;
}

function renderRadar(): HTMLElement {
  const c = document.createElement('div');
  c.className = 'rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-elevated)] p-4';
  // 5 mock scores 0..1, ordered: Vocabulaire, Grammaire, Prononciation, Écoute, Tâche
  const scores = [0.85, 0.65, 0.40, 0.70, 0.55];
  const labels = tracks.map((t) => t.title);
  const size = 220;
  const cx = size / 2;
  const cy = size / 2 + 10;
  const r = 75;
  const points = scores.map((s, i) => {
    const angle = -Math.PI / 2 + (i * 2 * Math.PI) / scores.length;
    return [cx + Math.cos(angle) * r * s, cy + Math.sin(angle) * r * s] as const;
  });
  const grid = [0.25, 0.5, 0.75, 1].map((g) => {
    const poly = labels.map((_, i) => {
      const angle = -Math.PI / 2 + (i * 2 * Math.PI) / labels.length;
      return `${cx + Math.cos(angle) * r * g},${cy + Math.sin(angle) * r * g}`;
    }).join(' ');
    return `<polygon points="${poly}" fill="none" stroke="currentColor" stroke-opacity="0.15"/>`;
  }).join('');
  const labelEls = labels.map((label, i) => {
    const angle = -Math.PI / 2 + (i * 2 * Math.PI) / labels.length;
    const lx = cx + Math.cos(angle) * (r + 18);
    const ly = cy + Math.sin(angle) * (r + 18);
    const anchor = Math.abs(Math.cos(angle)) < 0.1 ? 'middle' : Math.cos(angle) > 0 ? 'start' : 'end';
    return `<text x="${lx}" y="${ly}" text-anchor="${anchor}" font-size="10" fill="currentColor" opacity="0.7" dominant-baseline="middle">${label}</text>`;
  }).join('');
  const polyData = points.map(([x, y]) => `${x},${y}`).join(' ');

  c.innerHTML = `
    <h3 class="text-sm font-medium">Votre équilibre cette semaine</h3>
    <p class="text-xs text-[color:var(--color-ink-muted)] mb-2">Répartition de vos efforts</p>
    <svg viewBox="0 0 ${size} ${size + 20}" class="w-full text-[color:var(--color-ink)]">
      ${grid}
      <polygon points="${polyData}" fill="var(--color-fjord-500)" fill-opacity="0.3" stroke="var(--color-fjord-600)" stroke-width="1.5"/>
      ${points.map(([x, y]) => `<circle cx="${x}" cy="${y}" r="2.5" fill="var(--color-fjord-600)"/>`).join('')}
      ${labelEls}
    </svg>
  `;
  return c;
}
